import { z } from "zod/v4";
import { createEndpointBase } from "./core";
import { StepSchemas } from "../ea.schema";
import { CountResponseSchema } from "../ea.schema.endpoints";
import { GetRequestBaseResponseSchema, StepsFilterSchema } from "../ea.schema.query";

const base = "steps";
const steps = createEndpointBase(base);

export const stepsEndpoints = {
  stepsGet: steps({
    method: "GET",
    path: "",
    request: z.object({ filter: StepsFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(
      StepSchemas["general"].extend({ linkedLineNum: z.string() }),
    ),
    description: "Retrieves a list of script steps with filtering support.",
  }),
  stepsCount: steps({
    method: "GET",
    path: "count",
    request: z.object({ filter: StepsFilterSchema.pick({ where: true }).optional() }).optional(),
    response: CountResponseSchema,
    description: "Returns the total number of script steps matching the filters.",
  }),
  stepsCreate: steps({
    method: "POST",
    path: "",
    request: StepSchemas["createData"],
    response: z.any(),
    description: "Creates a new script step.",
  }),
  stepsCreateMany: steps({
    method: "POST",
    path: "bulk",
    request: z.object({ steps: z.array(StepSchemas["createData"]), startLineNum: z.number() }),
    response: z.any(),
    description: "Creates multiple script steps at once.",
  }),
  stepsUpdate: steps({
    method: "PATCH",
    path: ":id",
    request: StepSchemas["update"],
    response: z.any(),
    description: "Updates an existing script step.",
  }),
  stepsUpdateAll: steps({
    method: "PATCH",
    path: "",
    request: z.object({
      ids: z.array(z.number()),
      data: StepSchemas["update"].pick({
        isDisabled: true,
        isOptional: true,
        aiRecoveryData: true,
      }),
    }),
    response: z.any(),
    description: "Updates multiple script steps at once.",
  }),
  stepsReorder: steps({
    method: "PATCH",
    path: ":id/reorder",
    request: z.object({
      id: z.number(),
      selectedIds: z.array(z.number()),
      sourceId: z.number(),
      targetId: z.number(),
    }),
    response: z.array(StepSchemas["general"]),
    description: "Reorders a script step within its script.",
  }),
  stepsDelete: steps({
    method: "DELETE",
    path: ":id",
    request: z.object({ id: z.number() }),
    response: z.any(),
    description: "Permanently deletes a script step.",
  }),
  stepsDeleteMany: steps({
    method: "DELETE",
    path: "",
    request: z.object({ ids: z.array(z.number()) }),
    response: z.any(),
    description: "Permanently deletes multiple script steps.",
  }),
  stepsMove: steps({
    method: "POST",
    path: ":id/move",
    request: z.object({ lineNum: z.number(), id: z.number() }),
    response: z.any(),
    description: "Moves a script step to a specific line number.",
  }),
  stepsPaste: steps({
    method: "POST",
    path: "paste",
    request: z.object({
      taskScriptId: z.number(),
      fromStepId: z.number(),
      sourceScriptId: z.number(),
      copiedSteps: z.array(StepSchemas["general"].omit({ labelParams: true, codeTemplates: true })),
    }),
    response: z.any(),
    description: "Pastes copied script steps into a script.",
  }),
  stepsDownloadFile: steps({
    method: "POST",
    path: "downloadFile/:stepId",
    request: z.any(),
    response: z.any(),
    description: "Downloads a file associated with a script step.",
  }),
  stepsUploadTemporaryFile: steps({
    method: "POST",
    path: "uploadTemporaryFile",
    request: z.any(),
    response: z.any(),
    requestType: {} as { files?: any },
    description: "Uploads a temporary file for use in a script step.",
  }),
} as const;
